import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getErrorMessage, createApiClient } from '../utils/api';
import './Analytics.css';

interface PortfolioData {
  username: string;
  totalAccounts: number;
  totalBalance: number;
  percentage: number;
}

const Analytics: React.FC = () => {
  const [portfolioData, setPortfolioData] = useState<PortfolioData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [totalBalance, setTotalBalance] = useState(0);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const apiClient = createApiClient(15000); // 15 second timeout for analytics

  const COLORS = [
    '#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6',
    '#1abc9c', '#e67e22', '#34495e', '#16a085', '#27ae60',
    '#2980b9', '#8e44ad', '#c0392b', '#d35400', '#7f8c8d',
    '#2c3e50', '#95a5a6'
  ];

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      
      // Try to get selected users from configuration
      let selectedUsers: string[] = [];
      const savedConfig = localStorage.getItem('port-reallocation-config');
      
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          selectedUsers = config.map((c: any) => c.username);
        } catch (err) {
          console.error('Error parsing saved config:', err);
        }
      }
      
      // If no users are selected in config, fetch all users
      if (selectedUsers.length === 0) {
        const usersClient = createApiClient(10000);
        const usersResponse = await usersClient.get('/users');
        selectedUsers = usersResponse.data.users;
      }

      // Fetch analytics data for selected users
      const analyticsResponse = await apiClient.post('/analytics', {
        users: selectedUsers
      });

      const data = analyticsResponse.data.portfolio_data;
      const totals = analyticsResponse.data.totals;

      setPortfolioData(data);
      setTotalAccounts(totals.total_accounts);
      setTotalBalance(totals.total_balance);
      setError(null);
    } catch (err: any) {
      setError(getErrorMessage(err));
      console.error('Error loading analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="analytics">
        <div className="loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics">
        <div className="error">{error}</div>
        <button onClick={loadAnalytics} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="analytics">
      <div className="analytics-header">
        <h2>Portfolio Analytics</h2>
        <p>
          {portfolioData.length > 0 
            ? `Distribution of accounts across ${portfolioData.length} selected account manager${portfolioData.length > 1 ? 's' : ''}`
            : 'Distribution of accounts across account managers'}
        </p>
      </div>

      <div className="analytics-summary">
        <div className="summary-card">
          <div className="summary-label">Total Accounts</div>
          <div className="summary-value">{totalAccounts.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Total Balance</div>
          <div className="summary-value">{formatCurrency(totalBalance)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Account Managers</div>
          <div className="summary-value">{portfolioData.length}</div>
        </div>
      </div>

      <div className="charts-container">
        <div className="chart-section">
          <h3>Account Distribution by Manager</h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={portfolioData as any[]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.username}: ${formatPercent(entry.percentage)}`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="totalAccounts"
              >
                {portfolioData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: any, name: any, props: any) => [
                  `${value} accounts (${formatPercent(props.payload.percentage)})`,
                  props.payload.username
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-section">
          <h3>Balance Distribution by Manager</h3>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={portfolioData as any[]}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => `${entry.username}: ${formatCurrency(entry.totalBalance)}`}
                outerRadius={120}
                fill="#8884d8"
                dataKey="totalBalance"
              >
                {portfolioData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: any) => [formatCurrency(Number(value)), 'Balance']}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-section full-width">
          <h3>Accounts per Manager</h3>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={portfolioData as any[]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="username" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip 
                formatter={(value: any) => [`${value} accounts`, 'Total Accounts']}
              />
              <Bar dataKey="totalAccounts" fill="#3498db" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="analytics-table">
        <h3>Detailed Breakdown</h3>
        <table>
          <thead>
            <tr>
              <th>Account Manager</th>
              <th>Total Accounts</th>
              <th>Percentage</th>
              <th>Total Balance</th>
              <th>Avg Balance per Account</th>
            </tr>
          </thead>
          <tbody>
            {portfolioData.map((data, index) => (
              <tr key={index}>
                <td>{data.username}</td>
                <td>{data.totalAccounts.toLocaleString()}</td>
                <td>{formatPercent(data.percentage)}</td>
                <td>{formatCurrency(data.totalBalance)}</td>
                <td>{formatCurrency(data.totalBalance / data.totalAccounts)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td><strong>Total</strong></td>
              <td><strong>{totalAccounts.toLocaleString()}</strong></td>
              <td><strong>100%</strong></td>
              <td><strong>{formatCurrency(totalBalance)}</strong></td>
              <td><strong>{formatCurrency(totalBalance / totalAccounts)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default Analytics;
